import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";

const CATS = ["Market News", "Technical Analysis", "Fundamental Analysis", "Forex Analysis", "Gold Analysis", "Weekly Outlook"];
const BLANK = { title: "", title_ar: "", slug: "", category: CATS[0], excerpt: "", excerpt_ar: "", body: "", body_ar: "", featured_image: "", author: "MENAGroupFX Research Desk", reading_time: 4, status: "draft", featured: false, seo_title: "", seo_description: "" };

export default function ArticlesAdmin() {
  const qc = useQueryClient();
  const { data: articles = [] } = useQuery({ queryKey: ["admin-articles"], queryFn: async () => (await api.get("/admin/articles")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, reading_time: Number(form.reading_time) || 4 };
      if (editId) return api.put(`/admin/articles/${editId}`, payload);
      return api.post("/admin/articles", payload);
    },
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["admin-articles"] }); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/admin/articles/${id}`), onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-articles"] }); } });

  const openNew = () => { setForm(BLANK); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...BLANK, ...a }); setEditId(a.id); setOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-ink">Articles</h1><p className="mt-1 text-sm text-subtle">Manage market analysis content.</p></div>
        <Button onClick={openNew} className="bg-finblue hover:bg-finblueDark" data-testid="article-add"><Plus className="h-4 w-4" /> New Article</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-canvas"><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {articles.map((a) => (
              <TableRow key={a.id} data-testid={`article-row-${a.slug}`}>
                <TableCell className="font-medium text-ink">{a.title}</TableCell>
                <TableCell className="text-sm text-subtle">{a.category}</TableCell>
                <TableCell><span className={`rounded px-2 py-0.5 text-xs ${a.status === "published" ? "bg-success/10 text-success" : "bg-gold/15 text-[#8a6d2f]"}`}>{a.status}</span></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`article-edit-${a.slug}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del.mutate(a.id)} data-testid={`article-delete-${a.slug}`}><Trash2 className="h-4 w-4 text-danger" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Article" : "New Article"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Title" full><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="article-form-title" /></F>
            <F label="Slug (optional)"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} /></F>
            <F label="Category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="article-form-category"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </F>
            <F label="Featured Image URL"><Input value={form.featured_image} onChange={(e) => set("featured_image", e.target.value)} /></F>
            <F label="Reading time (min)"><Input type="number" value={form.reading_time} onChange={(e) => set("reading_time", e.target.value)} /></F>
            <F label="Excerpt" full><Textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></F>
            <F label="Body (HTML)" full><Textarea rows={8} value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="article-form-body" /></F>
            <div className="sm:col-span-2 rounded-lg border border-dashed border-hair p-3">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-finblue">العربية / Arabic (optional)</p>
              <div className="grid gap-4">
                <F label="Title (AR)" full><Input dir="rtl" value={form.title_ar} onChange={(e) => set("title_ar", e.target.value)} data-testid="article-form-title-ar" /></F>
                <F label="Excerpt (AR)" full><Textarea dir="rtl" rows={2} value={form.excerpt_ar} onChange={(e) => set("excerpt_ar", e.target.value)} /></F>
                <F label="Body AR (HTML)" full><Textarea dir="rtl" rows={6} value={form.body_ar} onChange={(e) => set("body_ar", e.target.value)} /></F>
              </div>
            </div>
            <F label="SEO Title"><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="article-form-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem></SelectContent></Select>
            </F>
            <F label="SEO Description" full><Textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></F>
            <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Feature this article</label>
          </div>
          <DialogFooter><Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-finblue hover:bg-finblueDark" data-testid="article-form-save">{save.isPending ? "Saving..." : "Save Article"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, full, children }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-sm">{label}</Label>{children}</div>;
}
